/**
 * Décisions de reconnexion, isolées du navigateur pour être testables.
 *
 * ## Pourquoi ce module existe
 *
 * La question « faut-il reprendre, ou faut-il échouer ? » se pose à plusieurs endroits de
 * `session.ts`, et se tromper coûte cher dans les deux sens :
 *
 * - **reprendre à tort** : l'utilisateur voit « reprise en cours… » pour un correspondant
 *   qui ne reviendra jamais — une barre figée, sans explication, exactement le défaut que
 *   le `PLAN.md` §1.3 reproche aux services existants ;
 * - **échouer à tort** : on jette un transfert de plusieurs gigaoctets déjà bien avancé,
 *   alors qu'une bascule Wi-Fi était passagère.
 *
 * La règle est donc explicite, et vérifiée séparément de l'interface.
 */

/** Ce que la session sait au moment de décider. */
export interface ReconnectInput {
  /** Phase courante de l'interface. */
  phase: string;
  /** Rôle de cette session. */
  role: 'sender' | 'receiver';
  /** Une tentative de reconnexion est-elle déjà en cours ? */
  alreadyReconnecting: boolean;
}

export type ReconnectDecision =
  /** Le lien est tombé pendant un transfert : on tente de reprendre. */
  | 'reconnect'
  /** Le pair est parti, mais rien n'était en cours : simple retour en attente. */
  | 'back-to-waiting'
  /** Rien à faire : le transfert est fini, ou une reprise est déjà engagée. */
  | 'ignore';

/**
 * Que faire quand le correspondant disparaît ?
 *
 * `alreadyReconnecting` évite d'armer une seconde minuterie et de réinitialiser un état
 * déjà en cours de rétablissement — un pair instable qui se déconnecte plusieurs fois de
 * suite ne doit pas prolonger indéfiniment la fenêtre de reprise.
 */
export function decideOnPeerLeft(input: ReconnectInput): ReconnectDecision {
  if (input.phase === 'reconnecting') return 'ignore';
  if (input.phase === 'transferring') return 'reconnect';

  // Hors transfert, un départ est bénin. Seul l'émetteur a quelque chose à faire : il
  // retourne en attente d'un nouveau destinataire. Le récepteur n'a rien à initier, et
  // l'émetteur peut revenir : forcer une phase ici effacerait cet état.
  if (input.role === 'sender') return 'back-to-waiting';
  return 'ignore';
}

/**
 * Une erreur technique doit-elle être montrée à l'utilisateur ?
 *
 * Pendant une reconnexion, l'émetteur et le récepteur échouent **tous les deux** — ils
 * dépendent du même canal. Afficher l'un des deux messages d'échec ferait croire le
 * transfert perdu alors que le système est en train de le rattraper.
 */
export function shouldSurfaceError(phase: string): boolean {
  return phase !== 'reconnecting';
}

/**
 * Une demande de reprise peut-elle être honorée sans redemander à l'utilisateur ?
 *
 * `showSaveFilePicker` exige une activation utilisateur transitoire : il est impossible
 * de le rappeler depuis un gestionnaire réseau. Si les destinations d'écriture n'ont pas
 * survécu à la coupure, la reprise automatique est donc **impossible**, et prétendre le
 * contraire bloquerait la session.
 */
export function canResumeWithoutPrompt(input: {
  hasSinks: boolean;
  hasResumeContext: boolean;
}): boolean {
  return input.hasSinks && input.hasResumeContext;
}

/**
 * La stratégie d'écriture permet-elle de conserver la progression ?
 *
 * `fs-access` et `directory` écrivent directement sur le disque : ce qui est reçu survit
 * à une coupure. `memory` garde tout dans l'onglet, `streamsaver` a déjà remis le fichier
 * au navigateur — dans les deux cas, il n'y a rien à reprendre.
 */
export function canPersistProgress(strategy: string | undefined): boolean {
  return strategy === 'fs-access' || strategy === 'directory';
}
